import { CircleMinus, Info, Settings2 } from "lucide-react"
import { useCallback, useId } from "react"
import {
  Controller,
  FormProvider,
  Path,
  useForm,
  useFormContext,
  useFormState,
  ControllerRenderProps,
  useWatch,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { RHFDirtyDraftRegistration } from "@/components/shared/dirty-draft-navigation"
import { ErrorMessage } from "@hookform/error-message"
import { zodResolver } from "@hookform/resolvers/zod"

import type { AttributeOptions, Speciality } from "@/types/educators/speciality"
import { Checkbox } from "@/components/ui/checkbox"
import { MultiCombobox, SelectOption } from "@/components/ui/combobox"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

import { Slider } from "@/components/ui/slider"
import { createAttributeOptions } from "@/constants/speciality"
import { useTOptions, VALIDATION_ERROR_KEYS } from "@/i18n"
import { ErrorHint } from "@/components/ui/errorhint"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  withAdhdOptions,
  withAutismOptions,
  withDepressionOptions,
  withExtentOptions,
  withEyesightOptions,
  withGradeOptions,
  withNaplanOptions,
  withParentEducationOptions,
  withParentOccupationOptions,
} from "@/lib/options"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ParseKeys } from "i18next"
import { SpecialityFormData, specialityFormSchema } from "./validation-schema"
import * as z from "zod"
import { FormProps, useFormProps } from "./page-props"
import { SelectInput } from "@/components/ui/select"
import { useScroller } from "@/components/shared/scroller"
import { useNavigate, useProps } from "@/lib/page-rendering"

const groupsMap = new Map<string, React.ElementType>([
  ["Religion", ReligionSection],
  ["Gender", GenderSection],
  ["Languages", LanguageSection],
  ["Intellectual Ability", IntellectualAbilitySection],
  ["Parent Socioeconomic", ParentSocioEconomicSection],
  ["Learning Styles", LearningStylesSection],
  ["Learning Preferences", LearningPreferencesSection],
  ["Other Information", OtherInformationSection],
  ["Socio Economic", SocioEconomicSection],
])

type Navigate = ReturnType<typeof useNavigate>

// Reselects the attribute sections: the entry is updated in place, so the
// form keeps its values, and the page scrolls to a section just added.
const handleSelect = (
  navigate: Navigate,
  previous: string[],
  selected: string[],
  scroll: () => void,
) => {
  const searchParams =
    selected.length === 0
      ? [["selected[]", ""]]
      : selected.map((value) => ["selected[]", value])

  navigate({
    href: `?${new URLSearchParams(searchParams).toString()}`,
    preserveScroll: selected.length < previous.length,
    replace: true,
  }).then(() => {
    if (selected.length >= previous.length) {
      scroll()
    }
  })
}

const handleRemove = (
  navigate: Navigate,
  selected: string[],
  attribute: FormProps["selected"][number],
  scroll: () => void,
) => {
  return (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    handleSelect(
      navigate,
      selected,
      selected.filter((item) => item !== attribute),
      scroll,
    )
  }
}

type FormValues = { speciality: SpecialityFormData }

export default function Form({
  onSubmit,
  children,
  id,
}: {
  onSubmit: (data: FormValues) => void
  children?: React.ReactNode
  id: string
}) {
  const { speciality, selected } = useFormProps()
  const { t } = useTranslation("common")

  const methods = useForm<FormValues>({
    defaultValues: { speciality },
    shouldUnregister: true,
    resolver: zodResolver(
      z
        .object({
          speciality: specialityFormSchema,
          team_profile_id: z.string().optional(),
          speciality_scope: z.enum(["personal", "team"]).optional(),
        })
        .refine(
          ({ team_profile_id, speciality_scope }) => {
            return speciality_scope === "team"
              ? team_profile_id !== undefined
              : true
          },
          {
            message: t("errors.required"),
            path: ["team_profile_id"],
          },
        ),
    ),
  })

  return (
    <FormProvider {...methods}>
      <RHFDirtyDraftRegistration />
      <form
        id={id}
        className="flex flex-col gap-8"
        onSubmit={methods.handleSubmit(onSubmit)}
        data-testid="speciality-form"
      >
        {children}
        <AttributesSection selected={selected} />
      </form>
    </FormProvider>
  )
}

export function DetailsSection({ children }: { children?: React.ReactNode }) {
  const { t } = useTranslation(["specialities"])
  const { register, control } = useFormContext<FormValues>()

  return (
    <Card>
      <CardContent className="p-6">
        <fieldset className="space-y-4">
          <Label htmlFor="name">{t("specialities:form.name")}</Label>
          <Input
            {...register("speciality.name")}
            id="name"
            placeholder="Speciality Name"
            required
          />
          <Label htmlFor="description">
            {t("specialities:form.description")}
          </Label>
          <Textarea
            {...register("speciality.description")}
            id="description"
            placeholder="Give speciality a short description"
          />
          <div className="flex flex-row gap-4">
            <Controller
              name={"speciality.teach_attributes_exclusively"}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-4">
                  <Label htmlFor="teach_attributes_exclusively">
                    {t("specialities:form.exclusive")}
                  </Label>
                  <RadioGroup
                    onValueChange={field.onChange}
                    // @ts-expect-error shadcn radio expects string but works with booleans
                    defaultValue={field.value}
                    required
                    id="teach_attributes_exclusively"
                    className="inline-flex gap-2"
                  >
                    <div className="inline-flex items-center gap-2">
                      <Label htmlFor={"exclusive_true"}>
                        {t("specialities:form.exclusive_true")}
                      </Label>
                      {/* @ts-expect-error shadcn radio expects string but works with booleans */}
                      <RadioGroupItem value={true} id={"exclusive_true"} />
                    </div>
                    <div className="inline-flex items-center gap-2">
                      <Label htmlFor={"exclusive_false"}>
                        {t("specialities:form.exclusive_false")}
                      </Label>
                      {/* @ts-expect-error shadcn radio expects string but works with booleans */}
                      <RadioGroupItem value={false} id={"exclusive_false"} />
                    </div>
                  </RadioGroup>
                </div>
              )}
            />
          </div>
          {children}
        </fieldset>
      </CardContent>
    </Card>
  )
}

function AttributesSection({ selected }: { selected: FormProps["selected"] }) {
  const { t } = useTranslation(["specialities"])
  const { scroll } = useScroller()
  const navigate = useNavigate()

  const attributeOptions = createAttributeOptions()
  const selectedValues = attributeOptions.filter((attribute) =>
    selected.includes(attribute.value),
  )

  const groupedValues = selectedValues.reduce<{
    [group: string]: typeof selectedValues
  }>((acc, item) => {
    if (!acc[item.group]) {
      acc[item.group] = []
    }
    acc[item.group].push(item)
    return acc
  }, {})

  const onMultiSelect = useCallback(
    (newSelected: SelectOption<string>[]) => {
      const selectedSimple = newSelected.map((item) => item.value)
      return handleSelect(navigate, selected, selectedSimple, scroll)
    },
    [navigate, selected, scroll],
  )

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-1">
            <Settings2 />
            <span id="attributes-select-header">
              {t("specialities:form.attributes_title")}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <span className="text-muted-foreground inline-flex items-center gap-2 text-sm">
            <Info className="size-4" />
            {t("specialities:form.attributes_info")}
          </span>
          <MultiCombobox
            className="h-full"
            options={attributeOptions}
            selected={selectedValues}
            onChange={onMultiSelect}
            selectPlaceholder={t("specialities:form.attributes_select")}
            ariaLabelledBy="attributes-select-header"
          />
        </CardContent>
      </Card>
      {Object.entries(groupedValues).map(([group, items]) => {
        const Component = groupsMap.get(group)
        if (!Component) return null
        return (
          <Card key={group}>
            <CardContent className="space-y-4 p-6">
              <Component selected={items} group={group} />
            </CardContent>
          </Card>
        )
      })}
    </>
  )
}

function SectionFieldset({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<"fieldset"> & {
  children: React.ReactNode
}) {
  const { setScrollNode } = useScroller()

  return (
    <fieldset
      className={cn("space-y-4", className)}
      ref={setScrollNode}
      {...props}
    >
      {children}
    </fieldset>
  )
}

function SectionHeader({
  children,
  className,
  id,
  attribute,
  ...props
}: React.ComponentProps<"span"> & {
  children: React.ReactNode
  attribute: FormProps["selected"][number]
}) {
  const { selected } = useProps<{ selected: string[] }>()
  const { t } = useTranslation(["common"])
  const { scroll } = useScroller()
  const navigate = useNavigate()
  return (
    <span
      id={id}
      className={cn(
        "inline-flex w-full items-center justify-between",
        className,
      )}
      {...props}
    >
      {children}
      <Button
        variant={"destructive-outline"}
        size={"sm"}
        className="h-8"
        onClick={handleRemove(navigate, selected, attribute, scroll)}
      >
        <CircleMinus />
        {t("actions.remove")}
      </Button>
    </span>
  )
}

function ReligionSection() {
  const { t } = useTranslation(["specialities"])
  const { speciality_religions } = useProps<{
    speciality_religions: Array<SelectOption<number>>
  }>()

  return (
    <SectionFieldset aria-labelledby="religion-section-title">
      <SectionHeader
        attribute={"speciality_religions"}
        className="text-xl font-semibold"
      >
        <span id="religion-section-title">
          {t("specialities:form.religion_title")}
        </span>
      </SectionHeader>
      <MultiSelectWithDescription
        selectName="speciality.speciality_religions_attributes"
        renderCombobox={({ field }) => {
          const selectedIds = field.value?.map((item) => item.religion_id) || []
          const selectedOptions = speciality_religions.filter((option) =>
            selectedIds.includes(option.value),
          )
          return (
            <MultiCombobox
              ariaLabelledBy="religion-section-title"
              options={speciality_religions}
              selected={selectedOptions}
              onChange={(newSelectedOptions) =>
                field.onChange(
                  newSelectedOptions.map(({ value }) => ({
                    religion_id: value,
                  })),
                )
              }
              selectPlaceholder={t("specialities:form.religion_select")}
            />
          )
        }}
        descriptionName="speciality.religion_notes"
        descriptionPlaceholder={t("specialities:form.religion_note")}
        descriptionLabel={t("specialities:form.description")}
      />
    </SectionFieldset>
  )
}

function MultiSelectWithDescription<SelectName extends Path<FormValues>>({
  selectName,
  renderCombobox,
  descriptionPlaceholder,
  descriptionName,
  descriptionLabel,
}: {
  selectName: SelectName
  renderCombobox: ({
    field,
  }: {
    field: ControllerRenderProps<FormValues, SelectName>
  }) => React.ReactElement
  descriptionPlaceholder?: string
  descriptionName: Path<FormValues>
  descriptionLabel: string
}) {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>()

  return (
    <>
      <div className="flex flex-col gap-2">
        <Controller
          name={selectName}
          control={control}
          shouldUnregister
          render={renderCombobox}
        />
        <ErrorMessage name={selectName} errors={errors} render={ErrorHint} />
      </div>
      <Label htmlFor={descriptionName}>{descriptionLabel}</Label>
      <Textarea
        {...register(descriptionName, { shouldUnregister: true })}
        id={descriptionName}
        placeholder={descriptionPlaceholder}
      />
    </>
  )
}

function GenderSection() {
  const { t } = useTranslation(["specialities", "common"])
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>()
  const { speciality_genders } = useProps<{
    speciality_genders: Array<SelectOption<number>>
  }>()

  return (
    <SectionFieldset aria-labelledby="gender-section-title">
      <SectionHeader
        id="gender-section-title"
        className="text-xl font-semibold"
        attribute="speciality_genders"
      >
        {t("specialities:form.gender_title")}
      </SectionHeader>
      <div>
        <Controller
          name="speciality.speciality_genders_attributes"
          control={control}
          shouldUnregister
          render={({ field }) => (
            <div className="space-x-2">
              {speciality_genders?.map((gender) => {
                const selected =
                  field.value?.map((item) => item.gender_id) || []
                const currentValue = field.value || []
                return (
                  <div
                    className="inline-flex items-center gap-2"
                    key={gender.value}
                  >
                    <Label htmlFor={`gender-${gender.value}`}>
                      {gender.label}
                    </Label>
                    <Checkbox
                      checked={selected.includes(gender.value)}
                      id={`gender-${gender.value}`}
                      onCheckedChange={(checked) =>
                        field.onChange(
                          checked
                            ? [...currentValue, { gender_id: gender.value }]
                            : currentValue.filter(
                                (item) => item.gender_id !== gender.value,
                              ),
                        )
                      }
                    />
                  </div>
                )
              })}
            </div>
          )}
        />

        <ErrorMessage
          name={"speciality.speciality_genders_attributes"}
          errors={errors}
          render={ErrorHint}
        />
      </div>
      <Label htmlFor="gender_notes">{t("specialities:form.description")}</Label>
      <Textarea
        {...register("speciality.gender_notes")}
        id="gender_notes"
        placeholder={t("specialities:form.gender_notes")}
      />
    </SectionFieldset>
  )
}

function PrimaryLanguageSection() {
  const { t } = useTranslation(["specialities"])
  const { speciality_languages } = useProps<{
    speciality_languages: Array<SelectOption<number>>
  }>()

  return (
    <SectionFieldset aria-labelledby="primary_language-section-title">
      <SectionHeader attribute="speciality_primary_languages">
        <span id="primary_language-section-title">
          {t("specialities:form.primary_language_title")}
        </span>
      </SectionHeader>
      <MultiSelectWithDescription
        selectName="speciality.speciality_primary_languages_attributes"
        renderCombobox={({ field }) => {
          const selectedIds = field.value?.map((item) => item.language_id) || []
          const selectedOptions = speciality_languages.filter((option) =>
            selectedIds.includes(option.value),
          )
          return (
            <MultiCombobox
              ariaLabelledBy="primary_language-section-title"
              options={speciality_languages}
              selected={selectedOptions}
              onChange={(newSelectedOptions) =>
                field.onChange(
                  newSelectedOptions.map(({ value }) => ({
                    language_id: value,
                  })),
                )
              }
              selectPlaceholder={t("specialities:form.primary_language_select")}
            />
          )
        }}
        descriptionName="speciality.primary_language_notes"
        descriptionPlaceholder={t("specialities:form.primary_language_note")}
        descriptionLabel={t("specialities:form.description")}
      />
    </SectionFieldset>
  )
}
function SecondaryLanguageSection() {
  const { t } = useTranslation(["specialities"])
  const { speciality_languages } = useProps<{
    speciality_languages: Array<SelectOption<number>>
  }>()
  return (
    <SectionFieldset aria-labelledby="secondary_language-section-title">
      <SectionHeader attribute="speciality_secondary_languages">
        <span id="secondary_language-section-title">
          {t("specialities:form.secondary_language_title")}
        </span>
      </SectionHeader>
      <MultiSelectWithDescription
        selectName="speciality.speciality_secondary_languages_attributes"
        renderCombobox={({ field }) => {
          const selectedIds = field.value?.map((item) => item.language_id) || []
          const selectedOptions = speciality_languages.filter((option) =>
            selectedIds.includes(option.value),
          )
          return (
            <MultiCombobox
              ariaLabelledBy="secondary_language-section-title"
              options={speciality_languages}
              selected={selectedOptions}
              onChange={(newSelectedOptions) =>
                field.onChange(
                  newSelectedOptions.map(({ value }) => ({
                    language_id: value,
                  })),
                )
              }
              selectPlaceholder={t(
                "specialities:form.secondary_language_select",
              )}
            />
          )
        }}
        descriptionName="speciality.secondary_language_notes"
        descriptionPlaceholder={t("specialities:form.secondary_language_note")}
        descriptionLabel={t("specialities:form.description")}
      />
    </SectionFieldset>
  )
}

type FromToPath<T = Path<FormValues>> =
  T extends `${infer Base}${".from" | ".to"}` ? Base : never

function FromToSelect<Value extends string>({
  name,
  fromLabel,
  toLabel,
  options,
}: {
  name: FromToPath
  fromLabel: string
  toLabel: string
  options: SelectOption<Value>[]
}) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>()
  const fromName = `${name}.from` satisfies Path<FormValues>
  const toName = `${name}.to` satisfies Path<FormValues>
  const htmlForFrom = useId()
  const htmlForTo = useId()

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="grow">
          <Label htmlFor={htmlForFrom}>{fromLabel}</Label>
          <Controller
            name={fromName}
            control={control}
            shouldUnregister
            render={({ field }) => (
              <SelectInput
                className="w-full"
                value={field.value as Value}
                onChange={field.onChange}
                options={options}
                id={htmlForFrom}
              />
            )}
          />
          <ErrorMessage name={fromName} errors={errors} render={ErrorHint} />
        </div>
        <div className="grow">
          <Label htmlFor={htmlForTo}>{toLabel}</Label>
          <Controller
            name={toName}
            control={control}
            shouldUnregister
            render={({ field }) => (
              <SelectInput
                className="w-full"
                value={field.value as Value}
                onChange={field.onChange}
                options={options}
                id={htmlForTo}
              />
            )}
          />
          <ErrorMessage name={toName} errors={errors} render={ErrorHint} />
        </div>
      </div>
    </>
  )
}

const GradeFromToSelect = withGradeOptions(FromToSelect)

function OverallQualitativeSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset aria-labelledby="qualitative-section-title">
      <SectionHeader
        id="qualitative-section-title"
        attribute="overall_qualitative_assessment"
      >
        {t("specialities:form.qualitative_title")}
      </SectionHeader>
      <GradeFromToSelect
        name="speciality.overall_qualitative_assessment_attributes.overall_qualitative"
        fromLabel={t("specialities:form.qualitative_from")}
        toLabel={t("specialities:form.qualitative_to")}
      />
    </SectionFieldset>
  )
}

function SchoolGradeSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset aria-labelledby="grade-section-title">
      <SectionHeader id="grade-section-title" attribute="subject_grade_range">
        {t("specialities:form.grade_title")}
      </SectionHeader>
      <GradeFromToSelect
        name="speciality.subject_grade_range_attributes.grade"
        fromLabel={t("specialities:form.grade_from")}
        toLabel={t("specialities:form.grade_to")}
      />
    </SectionFieldset>
  )
}

const NaplanFromToOptions = withNaplanOptions(FromToSelect)

function NapalanSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset aria-labelledby="naplan-section-title">
      <SectionHeader id="naplan-section-title" attribute="naplan_score">
        {t("specialities:form.naplan_title")}
      </SectionHeader>
      {(["numeracy", "reading", "writing", "spelling", "grammar"] as const).map(
        (scoreType) => (
          <NaplanFromToOptions
            key={scoreType}
            name={`speciality.naplan_score_attributes.naplan_score_${scoreType}`}
            fromLabel={t(`specialities:form.naplan_${scoreType}_from`)}
            toLabel={t(`specialities:form.naplan_${scoreType}_to`)}
          />
        ),
      )}
    </SectionFieldset>
  )
}

const EyesightFromToOptions = withEyesightOptions(FromToSelect)

function EyesightSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset aria-labelledby="eyesight-section-title">
      <SectionHeader id="eyesight-section-title" attribute="eyesight">
        {t("specialities:form.eyesight_title")}
      </SectionHeader>
      <EyesightFromToOptions
        name="speciality.eyesight_attributes.eyesight"
        fromLabel={t("specialities:form.eyesight_from")}
        toLabel={t("specialities:form.eyesight_to")}
      />
    </SectionFieldset>
  )
}

function GeneralOrSpecificOptions<Option extends string>({
  options,
  generalLabel,
  name,
}: {
  options: SelectOption<Option>[]
  generalLabel: string
  name: Path<FormValues>
}) {
  const { control } = useFormContext<FormValues>()
  const { errors } = useFormState<FormValues>()
  const booleans = (useWatch({ control, name }) || {}) as Record<
    Option,
    boolean
  >
  const optionValues = options.map((key) => key.value)
  const selected = optionValues.filter((value) => booleans[value])
  const buildBooleans = (newSelected: Option[]) =>
    Object.fromEntries(
      optionValues.map((value) => [value, newSelected.includes(value)]),
    )
  const generalCheckboxId = useId()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <>
          <ErrorMessage errors={errors} name={name} render={ErrorHint} />

          <div className="flex flex-row space-x-2">
            <Checkbox
              id={generalCheckboxId}
              checked={selected.length === options.length}
              onCheckedChange={(checked) =>
                checked
                  ? field.onChange(
                      Object.fromEntries(
                        options.map(({ value }) => [value, true]),
                      ),
                    )
                  : field.onChange(
                      Object.fromEntries(
                        options.map(({ value }) => [value, false]),
                      ),
                    )
              }
            />
            <Label htmlFor={generalCheckboxId}>{generalLabel}</Label>
          </div>

          {options.map(({ label, value }) => (
            <div key={value} className="flex flex-row space-x-2">
              <Checkbox
                id={value}
                checked={selected.includes(value)}
                onCheckedChange={(checked) => {
                  field.onChange(
                    buildBooleans(
                      checked
                        ? [...selected, value]
                        : selected.filter((item) => item !== value),
                    ),
                  )
                }}
              />
              <Label htmlFor={value}>{label}</Label>
            </div>
          ))}
        </>
      )}
    />
  )
}

const GeneralOrSpecificAdhdOptions = withAdhdOptions(GeneralOrSpecificOptions)

function ADHDSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset
      aria-labelledby="adhd-section-title"
      className="flex flex-col"
    >
      <SectionHeader id="adhd-section-title" attribute="adhd">
        {t("specialities:form.adhd_title")}
      </SectionHeader>
      <GeneralOrSpecificAdhdOptions
        name="speciality.adhd_attributes"
        generalLabel={"General ADHD (any of the below)"}
      />
    </SectionFieldset>
  )
}

const GeneralOrSpecificAutismOptions = withAutismOptions(
  GeneralOrSpecificOptions,
)

function AutismSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset
      aria-labelledby="autism-section-title"
      className="flex flex-col"
    >
      <SectionHeader id="autism-section-title" attribute="autism">
        {t("specialities:form.autism_title")}
      </SectionHeader>
      <GeneralOrSpecificAutismOptions
        name={"speciality.autism_attributes"}
        generalLabel={"General Autism (any of the below)"}
      />
    </SectionFieldset>
  )
}

const GeneralOrSpecificDepressionOptions = withDepressionOptions(
  GeneralOrSpecificOptions,
)

function DepressionSection() {
  const { t } = useTranslation(["specialities", "common"])

  return (
    <SectionFieldset
      aria-labelledby="depression-section-title"
      className="flex flex-col"
    >
      <SectionHeader id="depression-section-title" attribute="depression">
        {t("specialities:form.depression_title")}
      </SectionHeader>
      <GeneralOrSpecificDepressionOptions
        name={"speciality.depression_attributes"}
        generalLabel={"General Depression (any of the below)"}
      />
    </SectionFieldset>
  )
}

const ParentEducationFromToSelect = withParentEducationOptions(FromToSelect)
const ParentOccupationFromToSelect = withParentOccupationOptions(FromToSelect)

function ParentSocioEconomicSection() {
  const { t } = useTranslation(["specialities"])
  const { control } = useFormContext()
  const exclusive = useWatch({
    name: "speciality.teach_attributes_exclusively",
  })

  return (
    <SectionFieldset aria-labelledby="parent_socioeconomic-section-title">
      <SectionHeader
        id="parent_socioeconomic-section-title"
        attribute="parent_socio_economic"
        className="text-xl font-semibold"
      >
        {t("specialities:form.parent_socioeconomic-section-title")}
      </SectionHeader>
      <ParentEducationFromToSelect
        name="speciality.parent_socio_economic_attributes.socio_economic_parent_education"
        fromLabel={t("specialities:form.parent_education_from")}
        toLabel={t("specialities:form.parent_education_to")}
      />
      {exclusive ? (
        <Controller
          name="speciality.parent_socio_economic_attributes.socio_economic_parent_education_requires_both"
          control={control}
          shouldUnregister
          render={({ field }) => (
            <div className="flex flex-col gap-4">
              <Label
                htmlFor={"socio_economic_parent_education_requires_both"}
                id={"socio_economic_parent_education_requires_both"}
              >
                {t(`specialities:form.parent_education_requires_both`)}
              </Label>
              <RadioGroup
                onValueChange={(value) => field.onChange(value)}
                value={
                  typeof field.value === "boolean"
                    ? String(field.value)
                    : field.value
                }
                className="inline-flex gap-2"
                aria-labelledby={
                  "socio_economic_parent_education_requires_both"
                }
              >
                <div className="inline-flex items-center gap-2">
                  <Label
                    htmlFor={`socio_economic_parent_education_requires_both_true`}
                    className="font-normal"
                  >
                    {t("specialities:form.parent_true")}
                  </Label>
                  <RadioGroupItem
                    value={"true"}
                    id={`socio_economic_parent_education_requires_both_true`}
                  />
                </div>
                <div className="inline-flex items-center gap-2">
                  <Label
                    className="font-normal"
                    htmlFor={`socio_economic_parent_education_requires_both_false`}
                  >
                    {t("specialities:form.parent_false")}
                  </Label>
                  <RadioGroupItem
                    value={"false"}
                    id={`socio_economic_parent_education_requires_both_false`}
                  />
                </div>
              </RadioGroup>
            </div>
          )}
        />
      ) : null}
      <ParentOccupationFromToSelect
        name="speciality.parent_socio_economic_attributes.socio_economic_parent_occupation"
        fromLabel={t("specialities:form.parent_occupation_from")}
        toLabel={t("specialities:form.parent_occupation_to")}
      />
      {exclusive ? (
        <Controller
          name="speciality.parent_socio_economic_attributes.socio_economic_parent_occupation_requires_both"
          control={control}
          shouldUnregister
          render={({ field }) => (
            <div className="flex flex-col gap-4">
              <Label
                htmlFor={"socio_economic_parent_occupation_requires_both"}
                id={"socio_economic_parent_occupation_requires_both"}
              >
                {t(`specialities:form.parent_occupation_requires_both`)}
              </Label>
              <RadioGroup
                onValueChange={(value) => field.onChange(value)}
                value={
                  typeof field.value === "boolean"
                    ? String(field.value)
                    : field.value
                }
                className="inline-flex gap-2"
                aria-labelledby={
                  "socio_economic_parent_occupation_requires_both"
                }
              >
                <div className="inline-flex items-center gap-2">
                  <Label
                    className="font-normal"
                    htmlFor={`socio_economic_parent_occupation_requires_both_true`}
                  >
                    {t("specialities:form.parent_true")}
                  </Label>
                  <RadioGroupItem
                    value={"true"}
                    id={`socio_economic_parent_occupation_requires_both_true`}
                  />
                </div>
                <div className="inline-flex items-center gap-2">
                  <Label
                    className="font-normal"
                    htmlFor={`socio_economic_parent_occupation_requires_both_false`}
                  >
                    {t("specialities:form.parent_false")}
                  </Label>
                  <RadioGroupItem
                    value={"false"}
                    id={`socio_economic_parent_occupation_requires_both_false`}
                  />
                </div>
              </RadioGroup>
            </div>
          )}
        />
      ) : null}
    </SectionFieldset>
  )
}

type IntelligenceAttributes =
  | "wisc_v_attributes"
  | "wais_iv_attributes"
  | "wiat_iv_attributes"
  | "aeas_attributes"

type IntelligenceRangePaths<T = Path<SpecialityFormData>> =
  T extends `${infer Range}${".from" | ".to"}`
    ? Range extends `${string}${IntelligenceAttributes}${string}`
      ? Range
      : never
    : never

function IntelligenceElement({
  options,
  id,
  title,
  year,
  attribute,
}: {
  options: Array<IntelligenceRangePaths>
  id: string
  title: string
  year?: React.ReactNode
  attribute: FormProps["selected"][number]
}) {
  const { t } = useTranslation(["specialities", "common"])
  const {
    register,
    formState: { errors },
  } = useFormContext<FormValues>()

  return (
    <SectionFieldset aria-labelledby={id}>
      <SectionHeader id={id} attribute={attribute}>
        {title}
      </SectionHeader>
      <div>{year}</div>
      {options.map((option) => {
        return (
          <div className="flex w-full flex-row flex-wrap gap-4" key={option}>
            <div className="flex-1">
              <Label htmlFor={`${option}_from`}>
                {t(
                  `specialities:form.${option}_from` satisfies ParseKeys<
                    ["specialities"]
                  >,
                )}
              </Label>
              <Input
                {...register(`speciality.${option}.from`, {
                  valueAsNumber: true,
                  shouldUnregister: true,
                })}
                id={`${option}_from`}
                min={0}
                type="number"
              />
              <ErrorMessage
                name={`speciality.${option}.from`}
                errors={errors}
                render={ErrorHint}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor={`${option}_to`}>
                {t(
                  `specialities:form.${option}_to` satisfies ParseKeys<
                    ["specialities"]
                  >,
                )}
              </Label>
              <Input
                {...register(`speciality.${option}.to`, {
                  valueAsNumber: true,
                  shouldUnregister: true,
                })}
                id={`${option}_to`}
                min={0}
                type="number"
              />
              <ErrorMessage
                name={`speciality.${option}.to`}
                errors={errors}
                render={ErrorHint}
              />
            </div>
          </div>
        )
      })}
    </SectionFieldset>
  )
}

function WISCSection() {
  const { t } = useTranslation(["specialities", "common"])
  const {
    register,
    formState: { errors },
  } = useFormContext<FormValues>()
  const options = [
    "wisc_v_attributes.wisc_v_fsiq",
    "wisc_v_attributes.wisc_v_vci",
    "wisc_v_attributes.wisc_v_vsi",
    "wisc_v_attributes.wisc_v_wmi",
    "wisc_v_attributes.wisc_v_psi",
    "wisc_v_attributes.wisc_v_fri",
  ] as const satisfies Array<IntelligenceRangePaths>

  return (
    <IntelligenceElement
      id="wisc-section-title"
      title={t("specialities:form.wisc_title")}
      options={options}
      attribute="wisc_v"
      year={
        <>
          <Label htmlFor="wisc-year">{t("specialities:form.wisc_year")}</Label>
          <Input
            {...register("speciality.wisc_v_attributes.wisc_v_year", {
              shouldUnregister: true,
              required: t(VALIDATION_ERROR_KEYS.REQUIRED),
            })}
            id="wisc-year"
            type="Number"
          />
          <ErrorMessage
            name={"speciality.wisc_v_attributes.wisc_v_year"}
            errors={errors}
            render={ErrorHint}
          />
        </>
      }
    />
  )
}
function WAISSection() {
  const { t } = useTranslation(["specialities"])
  const options = [
    "wais_iv_attributes.wais_iv_full_scale_iq",
    "wais_iv_attributes.wais_iv_general_ability",
    "wais_iv_attributes.wais_iv_verbal_comprehension",
    "wais_iv_attributes.wais_iv_perceptual_reasoning",
    "wais_iv_attributes.wais_iv_working_memory",
    "wais_iv_attributes.wais_iv_proc_speed",
  ] as const satisfies Array<IntelligenceRangePaths>

  return (
    <IntelligenceElement
      id="wais-section-title"
      title={t("specialities:form.wais_title")}
      options={options}
      attribute="wais_iv"
    />
  )
}
function WIATSection() {
  const { t } = useTranslation(["specialities", "common"])
  const {
    register,
    formState: { errors },
  } = useFormContext<FormValues>()
  const options = [
    "wiat_iv_attributes.wiat_iv_total_achievement_composite",
    "wiat_iv_attributes.wiat_iv_word_reading",
    "wiat_iv_attributes.wiat_iv_reading_comprehension",
    "wiat_iv_attributes.wiat_iv_spelling",
    "wiat_iv_attributes.wiat_iv_sentence_composition",
    "wiat_iv_attributes.wiat_iv_math_problem_solving",
    "wiat_iv_attributes.wiat_iv_numerical_operations",
    "wiat_iv_attributes.wiat_iv_reading_composite",
    "wiat_iv_attributes.wiat_iv_written_expression_composite",
    "wiat_iv_attributes.wiat_iv_mathematics_composite",
    "wiat_iv_attributes.wiat_iv_basic_reading_composite",
    "wiat_iv_attributes.wiat_iv_decoding_composite",
    "wiat_iv_attributes.wiat_iv_reading_fluency_composite",
    "wiat_iv_attributes.wiat_iv_math_fluency_composite",
    "wiat_iv_attributes.wiat_iv_writing_fluency_composite",
    "wiat_iv_attributes.wiat_iv_oral_language_composite",
    "wiat_iv_attributes.wiat_iv_phonological_proc_composite",
    "wiat_iv_attributes.wiat_iv_ortho_proc_composite",
    "wiat_iv_attributes.wiat_iv_ortho_proc_extended_composite",
    "wiat_iv_attributes.wiat_iv_dyslexia_index_composite",
  ] as const satisfies Array<IntelligenceRangePaths>

  return (
    <IntelligenceElement
      id="wiat-section-title"
      title={t("specialities:form.wiat_title")}
      options={options}
      attribute="wiat_iv"
      year={
        <>
          <Label htmlFor="wiat-age">{t("specialities:form.wiat_age")}</Label>
          <Input
            {...register("speciality.wiat_iv_attributes.wiat_iv_age", {
              shouldUnregister: true,
              required: t(VALIDATION_ERROR_KEYS.REQUIRED),
            })}
            id="wiat-age"
            type="Number"
          />
          <ErrorMessage
            name={"speciality.wiat_iv_attributes.wiat_iv_age"}
            errors={errors}
            render={ErrorHint}
          />
        </>
      }
    />
  )
}

function AEASSection() {
  const { t } = useTranslation(["specialities", "common"])
  const options = [
    "aeas_attributes.aeas_score_reading",
    "aeas_attributes.aeas_score_spelling",
    "aeas_attributes.aeas_score_written_essay",
    "aeas_attributes.aeas_score_listening",
    "aeas_attributes.aeas_score_speaking",
    "aeas_attributes.aeas_score_overall",
  ] as const satisfies Array<IntelligenceRangePaths>

  return (
    <IntelligenceElement
      id="aeas-section"
      title={t("specialities:form.aeas_title")}
      options={options}
      attribute="aeas"
    />
  )
}

function SocioEconomicSection() {
  const { t } = useTranslation(["specialities", "common"])
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<FormValues>()
  return (
    <SectionFieldset aria-labelledby={"socio-economic-section-title"}>
      <SectionHeader
        id="socio-economic-section-title"
        attribute={"student_socio_economic"}
        className="text-xl font-semibold"
      >
        {t("specialities:form.socio_economic_title")}
      </SectionHeader>
      <div>
        <Label htmlFor={"socio_economic_other_comment"}>
          {t("specialities:form.socio_economic_other_comment")}
        </Label>
        <Textarea
          {...register(
            "speciality.student_socio_economic_attributes.socio_economic_other_comment",
            {
              shouldUnregister: true,
            },
          )}
          id={"socio_economic_other_comment"}
          placeholder={t("specialities:form.socio_economic_comment")}
        />
      </div>

      <Controller
        name={
          "speciality.student_socio_economic_attributes.socio_economic_aboriginal_torres_strait_islander"
        }
        shouldUnregister
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-4">
            <Label
              htmlFor="specialities:form.socio_economic_aboriginal_torres_strait_islander"
              id="specialities:form.socio_economic_aboriginal_torres_strait_islander"
            >
              {t(
                "specialities:form.socio_economic_aboriginal_torres_strait_islander",
              )}
            </Label>
            <RadioGroup
              onValueChange={field.onChange}
              aria-labelledby="specialities:form.socio_economic_aboriginal_torres_strait_islander"
              // @ts-expect-error shadcn radio expects string but works with booleans
              defaultValue={field.value ?? false}
              className="inline-flex gap-2"
            >
              <div className="inline-flex items-center gap-2">
                <Label htmlFor={"yes"}>
                  {t(
                    "specialities:form.socio_economic_aboriginal_torres_strait_islander_true",
                  )}
                </Label>
                {/* @ts-expect-error shadcn radio expects string but works with booleans */}
                <RadioGroupItem value={true} id={"yes"} />
              </div>
              <div className="inline-flex items-center gap-2">
                <Label htmlFor={"no"}>
                  {t(
                    "specialities:form.socio_economic_aboriginal_torres_strait_islander_false",
                  )}
                </Label>
                {/* @ts-expect-error shadcn radio expects string but works with booleans */}
                <RadioGroupItem value={false} id={"no"} />
              </div>
            </RadioGroup>
          </div>
        )}
      />
      <span>{t(`specialities:form.socio_economic_icsea_percentile`)}</span>
      <div className="flex w-full flex-row flex-wrap gap-4">
        <div className="grow">
          <Label htmlFor={"socio_economic_icsea_percentile_from"}>
            {t(`specialities:form.socio_economic_icsea_percentile_from`)}
          </Label>
          <Input
            {...register(
              "speciality.student_socio_economic_attributes.socio_economic_icsea_percentile.from",
              {
                valueAsNumber: true,
                shouldUnregister: true,
              },
            )}
            id={"socio_economic_icsea_percentile_from"}
            type="number"
          />
          <ErrorMessage
            name={
              "speciality.student_socio_economic_attributes.socio_economic_icsea_percentile.from"
            }
            errors={errors}
            render={ErrorHint}
          />
        </div>
        <div className="grow">
          <Label htmlFor={"socio_economic_icsea_percentile_to"}>
            {t(`specialities:form.socio_economic_icsea_percentile_to`)}
          </Label>
          <Input
            {...register(
              "speciality.student_socio_economic_attributes.socio_economic_icsea_percentile.to",
              {
                valueAsNumber: true,
                shouldUnregister: true,
              },
            )}
            id={"socio_economic_icsea_percentile_to"}
            type="number"
          />
          <ErrorMessage
            name={
              "speciality.student_socio_economic_attributes.socio_economic_icsea_percentile.to"
            }
            errors={errors}
            render={ErrorHint}
          />
        </div>
      </div>
      <Textarea
        {...register(
          "speciality.student_socio_economic_attributes.socio_economic_icsea_comment",
          {
            shouldUnregister: true,
          },
        )}
        placeholder={t("specialities:form.socio_economic_comment")}
      />
    </SectionFieldset>
  )
}

function LearningStyleDescriptionSection() {
  const { t } = useTranslation(["specialities"])
  const { register } = useFormContext<FormValues>()
  return (
    <SectionFieldset aria-labelledby="learning_style_description_label">
      <SectionHeader attribute="learning_style_description">
        <Label
          htmlFor="learning_style_description"
          id="learning_style_description_label"
        >
          {t("specialities:form.learning_style_description")}
        </Label>
      </SectionHeader>
      <Textarea
        {...register("speciality.learning_style_description", {
          shouldUnregister: true,
        })}
        id="learning_style_description"
      />
    </SectionFieldset>
  )
}

function LearningStylesSection({
  selected,
  group,
}: {
  selected: AttributeOptions
  group: string
}) {
  const attributesMap = new Map<string, React.ElementType>([
    ["learning_style_description", LearningStyleDescriptionSection],
    ["learning_style_vark", VarkSection],
    ["learning_style_gardner", GardenersSection],
    ["learning_style_kolb", KolbsTheorySection],
  ])

  return (
    <div className="space-y-4">
      <span className="text-xl font-semibold">{group}</span>
      {selected.map((item) => {
        const Component = attributesMap.get(item.value)
        return Component ? <Component key={item.value} /> : null
      })}
    </div>
  )
}

const ExtentFromToOptions = withExtentOptions(FromToSelect)

function VarkSection() {
  const { t } = useTranslation(["specialities"])
  const rows = [
    {
      value: "speciality.learning_style_vark_attributes.learning_style_visual",
      label: "specialities:form.learning_style_visual",
    },
    {
      value:
        "speciality.learning_style_vark_attributes.learning_style_auditory",
      label: "specialities:form.learning_style_auditory",
    },
    {
      value:
        "speciality.learning_style_vark_attributes.learning_style_kinesthetic",
      label: "specialities:form.learning_style_kinesthetic",
    },
    {
      value:
        "speciality.learning_style_vark_attributes.learning_style_read_write",
      label: "specialities:form.learning_style_read_write",
    },
  ] as const
  return (
    <SectionFieldset aria-labelledby="vark-section-title">
      <SectionHeader id="vark-section-title" attribute={"learning_style_vark"}>
        {t("specialities:form.vark_title")}
      </SectionHeader>
      {rows.map(({ value, label }) => (
        <ExtentFromToOptions
          key={value}
          name={value}
          fromLabel={t(`${label}_from`)}
          toLabel={t(`${label}_to`)}
        />
      ))}
    </SectionFieldset>
  )
}

function GardenersSection() {
  const { t } = useTranslation(["specialities"])

  const rows = [
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_linguistic",
      label: "specialities:form.learning_style_linguistic",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_logical",
      label: "specialities:form.learning_style_logical",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_musical",
      label: "specialities:form.learning_style_musical",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_spatial",
      label: "specialities:form.learning_style_spatial",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_body_kinesthetic",
      label: "specialities:form.learning_style_body_kinesthetic",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_interpersonal",
      label: "specialities:form.learning_style_interpersonal",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_intrapersonal",
      label: "specialities:form.learning_style_intrapersonal",
    },
    {
      value:
        "speciality.learning_style_gardner_attributes.learning_style_naturalistic",
      label: "specialities:form.learning_style_naturalistic",
    },
  ] as const

  return (
    <SectionFieldset aria-labelledby="gardner-section-title">
      <SectionHeader
        id="gardner-section-title"
        attribute={"learning_style_gardner"}
      >
        {t("specialities:form.gardner_title")}
      </SectionHeader>
      {rows.map(({ value, label }) => (
        <ExtentFromToOptions
          key={value}
          name={value}
          fromLabel={t(`${label}_from`)}
          toLabel={t(`${label}_to`)}
        />
      ))}
    </SectionFieldset>
  )
}

function KolbsTheorySection() {
  const { t } = useTranslation(["specialities"])

  const rows = [
    {
      value:
        "speciality.learning_style_kolb_attributes.learning_style_concrete_exp",
      label: "specialities:form.learning_style_concrete_exp",
    },
    {
      value:
        "speciality.learning_style_kolb_attributes.learning_style_reflective_obs",
      label: "specialities:form.learning_style_reflective_obs",
    },
    {
      value:
        "speciality.learning_style_kolb_attributes.learning_style_abstract_conc",
      label: "specialities:form.learning_style_abstract_conc",
    },
    {
      value:
        "speciality.learning_style_kolb_attributes.learning_style_active_exp",
      label: "specialities:form.learning_style_active_exp",
    },
  ] as const

  return (
    <SectionFieldset aria-labelledby="kolbs-section-title">
      <SectionHeader id="kolbs-section-title" attribute={"learning_style_kolb"}>
        {t("specialities:form.kolbs_title")}
      </SectionHeader>
      {rows.map(({ value, label }) => (
        <ExtentFromToOptions
          key={value}
          name={value}
          fromLabel={t(`${label}_from`)}
          toLabel={t(`${label}_to`)}
        />
      ))}
    </SectionFieldset>
  )
}

function LearningPreferencesSection({ group }: { group: string }) {
  const { t } = useTranslation(["specialities", "common"])
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<FormValues>()
  const options = useTOptions(
    "learning_preference_extent_options",
    "student_attributes_options",
  )
  const learningPreferenceRadios = [
    "learning_preference_attributes.learning_preference_individual",
    "learning_preference_attributes.learning_preference_group",
    "learning_preference_attributes.learning_preference_like_students",
    "learning_preference_attributes.learning_preference_diverse_students",
  ] as const satisfies Array<Path<Speciality>>

  const learningPreferenceSliders = [
    "learning_preference_attributes.learning_preference_teacher_centered",
    "learning_preference_attributes.learning_preference_structured",
    "learning_preference_attributes.learning_preference_technology_enhanced",
    "learning_preference_attributes.learning_preference_by_doing",
  ] as const satisfies Array<Path<Speciality>>

  return (
    <SectionFieldset aria-labelledby="learning_preferences-section-title">
      <SectionHeader
        id="learning_preferences-section-title"
        attribute="learning_preference"
      >
        <span className="text-lg font-semibold">{group}</span>
      </SectionHeader>
      <Label htmlFor="learning_preference_description">
        {t("specialities:form.general_description")}
      </Label>
      <Textarea
        {...register(
          "speciality.learning_preference_attributes.learning_preference_description",
          {
            shouldUnregister: true,
          },
        )}
        id="learning_preference_description"
      />
      {learningPreferenceRadios.map((name) => (
        <Controller
          key={name}
          name={`speciality.${name}`}
          control={control}
          shouldUnregister
          render={({ field }) => (
            <div className="flex flex-col gap-4">
              <Label htmlFor={name} id={name}>
                {t(`specialities:form.${name}`)}
              </Label>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value ?? "both"}
                className="inline-flex gap-2"
                aria-labelledby={name}
              >
                {options.map((option) => (
                  <div
                    className="inline-flex items-center gap-2"
                    key={option.value}
                  >
                    <Label htmlFor={`${name}_${option.value}`}>
                      {option.label}
                    </Label>
                    <RadioGroupItem
                      value={option.value}
                      id={`${name}_${option.value}`}
                    />
                  </div>
                ))}
              </RadioGroup>
              <ErrorMessage
                name={`speciality.${name}`}
                errors={errors}
                render={ErrorHint}
              />
            </div>
          )}
        />
      ))}

      {learningPreferenceSliders.map((name) => (
        <Controller
          key={name}
          shouldUnregister
          control={control}
          name={`speciality.${name}`}
          render={({ field }) => (
            <div className="flex flex-col gap-4">
              <Label htmlFor={name} id={name}>
                {t(`specialities:form.${name}`)}
              </Label>
              <Slider
                id={name}
                name={name}
                value={[field.value?.from ?? 0, field.value?.to ?? 10]}
                onValueChange={(val: number[]) =>
                  field.onChange({ from: val[0], to: val[1] })
                }
                min={0}
                max={10}
                step={1}
              />
              <div className="flex flex-row justify-between">
                <Label className="text-muted-foreground text-xs">
                  {t(`specialities:form.${name}_lower`)}
                </Label>
                <Label className="text-muted-foreground text-xs">
                  {t(`specialities:form.${name}_upper`)}
                </Label>
              </div>
              <ErrorMessage
                name={`speciality.${name}`}
                errors={errors}
                render={ErrorHint}
              />
            </div>
          )}
        />
      ))}
    </SectionFieldset>
  )
}

function LanguageSection({
  selected,
  group,
}: {
  selected: AttributeOptions
  group: string
}) {
  return (
    <div className="space-y-4">
      <span className="text-xl font-semibold">{group}</span>
      {selected.map((item) => {
        if (item.value === "speciality_primary_languages") {
          return <PrimaryLanguageSection key={item.value} />
        }
        return <SecondaryLanguageSection key={item.value} />
      })}
    </div>
  )
}

function IntellectualAbilitySection({
  selected,
  group,
}: {
  selected: AttributeOptions
  group: string
}) {
  const attributesMap = new Map<string, React.ElementType>([
    ["overall_qualitative_assessment", OverallQualitativeSection],
    ["subject_grade_range", SchoolGradeSection],
    ["naplan_score", NapalanSection],
    ["wisc_v", WISCSection],
    ["wiat_iv", WIATSection],
    ["wais_iv", WAISSection],
    ["aeas", AEASSection],
  ])

  return (
    <div className="space-y-4">
      <span className="text-xl font-semibold">{group}</span>
      {selected.map((item) => {
        const Component = attributesMap.get(item.value)

        return Component ? <Component key={item.value} /> : null
      })}
    </div>
  )
}

function OtherInformationSection({
  selected,
  group,
}: {
  selected: AttributeOptions
  group: string
}) {
  const attributesMap = new Map<string, React.ElementType>([
    ["eyesight", EyesightSection],
    ["adhd", ADHDSection],
    ["autism", AutismSection],
    ["depression", DepressionSection],
  ])

  return (
    <div className="space-y-4">
      <span className="text-xl font-semibold">{group}</span>
      {selected.map((item) => {
        const Component = attributesMap.get(item.value)
        return Component ? <Component key={item.value} /> : null
      })}
    </div>
  )
}
